// =============================================================================
// MeuDriver — Batch O3: useReschedule
// Hooks para solicitar e responder pedidos de reagendamento de aulas.
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export interface RescheduleRequest {
  id: string
  booking_id: string
  requested_by: string
  new_scheduled_at: string
  reason: string | null
  status: 'pending' | 'approved' | 'rejected'
  responded_at: string | null
  created_at: string
}

export interface RequestReschedulePayload {
  booking_id: string
  new_time: string        // ISO TIMESTAMPTZ
  reason?: string | null
}

export interface RespondReschedulePayload {
  request_id: string
  approved: boolean
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const rescheduleKeys = {
  all: ['reschedule-requests'] as const,
  byBooking: (bookingId: string) => [...rescheduleKeys.all, 'booking', bookingId] as const,
}

// ---------------------------------------------------------------------------
// useRescheduleRequests — lista pedidos de reagendamento de um agendamento
// ---------------------------------------------------------------------------

export function useRescheduleRequests(bookingId: string | undefined) {
  return useQuery<RescheduleRequest[]>({
    queryKey: rescheduleKeys.byBooking(bookingId ?? ''),
    enabled: Boolean(bookingId),
    staleTime: 1000 * 30,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reschedule_requests')
        .select('*')
        .eq('booking_id', bookingId!)
        .order('created_at', { ascending: false })

      if (error) throw error
      return (data ?? []) as RescheduleRequest[]
    },
  })
}

// ---------------------------------------------------------------------------
// useRequestReschedule — cria pedido de reagendamento via RPC
// ---------------------------------------------------------------------------

export function useRequestReschedule() {
  const qc = useQueryClient()

  return useMutation<string, Error, RequestReschedulePayload>({
    mutationFn: async ({ booking_id, new_time, reason }) => {
      const { data, error } = await supabase.rpc('request_reschedule', {
        p_booking_id: booking_id,
        p_new_time: new_time,
        p_reason: reason ?? null,
      })
      if (error) throw new Error(error.message)
      return data as string
    },
    onSuccess: (_, { booking_id }) => {
      qc.invalidateQueries({ queryKey: rescheduleKeys.byBooking(booking_id) })
      toast.success('Pedido de reagendamento enviado.')
    },
    onError: (err) => {
      toast.error(err.message)
    },
  })
}

// ---------------------------------------------------------------------------
// useRespondReschedule — aprova ou rejeita pedido de reagendamento via RPC
// ---------------------------------------------------------------------------

export function useRespondReschedule(bookingId?: string) {
  const qc = useQueryClient()

  return useMutation<void, Error, RespondReschedulePayload>({
    mutationFn: async ({ request_id, approved }) => {
      const { error } = await supabase.rpc('respond_reschedule', {
        p_request_id: request_id,
        p_approved: approved,
      })
      if (error) throw new Error(error.message)
    },
    onSuccess: (_, { approved }) => {
      if (bookingId) {
        qc.invalidateQueries({ queryKey: rescheduleKeys.byBooking(bookingId) })
        // Invalida também o booking para refletir o novo horário se aprovado
        qc.invalidateQueries({ queryKey: ['booking', bookingId] })
        qc.invalidateQueries({ queryKey: ['bookings'] })
      }
      toast.success(approved ? 'Reagendamento aprovado.' : 'Reagendamento recusado.')
    },
    onError: (err) => {
      toast.error(err.message)
    },
  })
}
